import { useRouter } from 'expo-router';
import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import fetchSelectChallenge, {
  SelectChallengeData,
} from 'features/auth/resetPassword/utils/fetchSelectChallenge/fetchSelectChallenge';
import { OtpFlowKey } from 'features/otp/constants/OtpFlowKey';
import { InformationScreenTypeEnum } from 'features/otp/information/InformationScreenTypeEnum';
import { useContext, useState } from 'react';
import { FeatureFlagKey } from 'services/statsig/types';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { getClientErrorResponse } from 'shared/utils/errors/getMaxLimitErrorResponse';
import { navigateToMaxLimitError } from 'shared/utils/errors/navigateToMaxLimitError';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

export function useSelectChallenge(email?: string) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const { getVaroSessionId, idToken } =
    useContext<AuthContextProps>(AuthContext);
  const authUrl = useAuthUrl(
    FeatureFlagKey.SELECT_CHALLENGE_USE_K8S_AUTH_SERVICE,
  );

  const selectChallenge = async (data: SelectChallengeData) => {
    setLoading(true);

    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    try {
      const {
        uiData = null,
        errorCode,
        message,
        title,
        challengeState,
        challengeType,
      } = await fetchSelectChallenge(
        authUrl,
        {
          ...data,
        },
        headers,
      );

      setLoading(false);

      if (uiData === null || errorCode) {
        router.navigate({
          pathname: '/otp/error',
          params: {
            screenType: getErrorScreen(errorCode),
            message: message,
            title: title,
          },
        });
      } else {
        if (uiData.maskedPhone === '' && !data.isCallForResendCode) {
          router.navigate({
            pathname: '/reset-password/check-email',
            params: {
              is1FA: String(data.allow1FA),
              flowKey: OtpFlowKey.FORGOT_PASSWORD_1FA,
              challengeId: data.challengeId,
              identityId: data.identityId,
              email,
            },
          });
        } else if (!data.isCallForResendCode) {
          router.navigate({
            pathname: '/reset-password/enter-otp',
            params: {
              flowKey: OtpFlowKey.FORGOT_PASSWORD_1FA,
              lastFourDigitPhoneNumber: uiData.maskedPhone,
              challengeId: data.challengeId,
              identityId: data.identityId,
            },
          });
        }

        return { challengeState, challengeType, errorCode, message, title };
      }
    } catch (error) {
      setLoading(false);

      const expiredCodeResponse = getClientErrorResponse(error, 6);
      if (expiredCodeResponse && expiredCodeResponse.title) {
        router.navigate({
          pathname: '/otp/error',
          params: {
            screenType: InformationScreenTypeEnum.LinkExpired,
            message: expiredCodeResponse.message,
            title: expiredCodeResponse.title,
          },
        });
        return;
      }

      if (navigateToMaxLimitError(router, error)) {
        return;
      }

      logErrorWithContext('SelectChallenge error: ' + error, {
        featureArea: FeatureArea.AUTHENTICATION,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.CRITICAL,
      });

      router.navigate({
        pathname: '/otp/error',
        params: { screenType: InformationScreenTypeEnum.Error },
      });
    }
  };

  return {
    selectChallenge,
    loading,
  };
}

const getErrorScreen = (errorCode: number | undefined) => {
  if (!errorCode) {
    return InformationScreenTypeEnum.Error;
  }
  switch (errorCode) {
    case 6:
      return InformationScreenTypeEnum.LinkExpired;
    case 4:
      return InformationScreenTypeEnum.MaxLimitReached;
    default:
      return InformationScreenTypeEnum.Error;
  }
};
